import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (!tokens?.idToken) {
      return null;
    }

    const idToken = tokens.idToken;
    const currentTime = Math.floor(Date.now() / 1000);
    if (idToken.payload.exp <= currentTime) {
      return null;
    }

    return idToken.toString();
  } catch (error) {
    console.error('[cometchatApi] Failed to get auth token:', error);
    return null;
  }
};

const cometchatRequest = async (endpoint, { method = 'GET', body } = {}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
  }

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  if (body !== undefined && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const responseText = await response.text();

  if (!response.ok) {
    let message = responseText;
    try {
      const parsed = JSON.parse(responseText);
      message = parsed.error || parsed.message || responseText;
    } catch {
      // keep raw text
    }

    const error = new Error(message || `HTTP ${response.status}`);
    error.status = response.status;
    error.endpoint = endpoint;
    throw error;
  }

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

/** GET /cometchat/groups — unified org + site list */
export const getCometchatGroups = async ({ hasGroupOnly = false } = {}) => {
  const query = hasGroupOnly ? '?has_group_only=true' : '';
  return cometchatRequest(`/cometchat/groups${query}`);
};

/** GET /cometchat/sites */
export const getCometchatSites = async ({ hasGroupOnly = false } = {}) => {
  const query = hasGroupOnly ? '?has_group_only=true' : '';
  return cometchatRequest(`/cometchat/sites${query}`);
};

/** GET /cometchat/sites/{site_id}/group */
export const getSiteGroup = async siteId => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group`);
};

/** POST /cometchat/sites/{site_id}/group/ensure */
export const ensureSiteGroup = async siteId => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/ensure`, {
    method: 'POST',
    body: {},
  });
};

/** PATCH /cometchat/sites/{site_id}/group — rename */
export const renameSiteGroup = async (siteId, name) => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group`, {
    method: 'PATCH',
    body: { name },
  });
};

/** POST /cometchat/sites/{site_id}/group/sync */
export const syncSiteGroup = async (siteId, { immediate_removals = [] } = {}) => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/sync`, {
    method: 'POST',
    body: { immediate_removals },
  });
};

/** PATCH /cometchat/sites/{site_id}/group/settings */
export const updateSiteGroupSettings = async (
  siteId,
  shiftMembershipEnabled
) => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/settings`, {
    method: 'PATCH',
    body: { shift_membership_enabled: shiftMembershipEnabled },
  });
};

/** DELETE /cometchat/sites/{site_id}/group */
export const deleteSiteGroup = async siteId => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group`, {
    method: 'DELETE',
  });
};

/** GET /cometchat/sites/{site_id}/group/members */
export const getSiteGroupMembers = async siteId => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/members`);
};

/** POST /cometchat/sites/{site_id}/group/members */
export const addSiteGroupMembers = async (siteId, userIds) => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/members`, {
    method: 'POST',
    body: { user_ids: userIds },
  });
};

/** DELETE /cometchat/sites/{site_id}/group/members */
export const removeSiteGroupMembers = async (siteId, userIds) => {
  return cometchatRequest(`/cometchat/sites/${siteId}/group/members`, {
    method: 'DELETE',
    body: { user_ids: userIds },
  });
};

/** GET /cometchat/tenant/settings */
export const getTenantCometchatSettings = async () => {
  return cometchatRequest('/cometchat/tenant/settings');
};

/** PATCH /cometchat/tenant/settings */
export const updateTenantCometchatSettings = async shiftMembershipEnabled => {
  return cometchatRequest('/cometchat/tenant/settings', {
    method: 'PATCH',
    body: { shift_membership_enabled: shiftMembershipEnabled },
  });
};

/** GET /cometchat/tenant/group */
export const getTenantGroup = async () => {
  return cometchatRequest('/cometchat/tenant/group');
};

/** POST /cometchat/tenant/group/ensure */
export const ensureTenantGroup = async ({ name } = {}) => {
  const body = name ? { name } : {};
  return cometchatRequest('/cometchat/tenant/group/ensure', {
    method: 'POST',
    body,
  });
};

/** PATCH /cometchat/tenant/group — rename */
export const renameTenantGroup = async name => {
  return cometchatRequest('/cometchat/tenant/group', {
    method: 'PATCH',
    body: { name },
  });
};

/** DELETE /cometchat/tenant/group */
export const deleteTenantGroup = async () => {
  return cometchatRequest('/cometchat/tenant/group', { method: 'DELETE' });
};

/** POST /cometchat/tenant/group/sync */
export const syncTenantGroup = async ({ immediate_removals = [] } = {}) => {
  return cometchatRequest('/cometchat/tenant/group/sync', {
    method: 'POST',
    body: { immediate_removals },
  });
};

/** GET /cometchat/tenant/group/members */
export const getTenantGroupMembers = async () => {
  return cometchatRequest('/cometchat/tenant/group/members');
};

/** POST /cometchat/tenant/group/members */
export const addTenantGroupMembers = async userIds => {
  return cometchatRequest('/cometchat/tenant/group/members', {
    method: 'POST',
    body: { user_ids: userIds },
  });
};

/** DELETE /cometchat/tenant/group/members */
export const removeTenantGroupMembers = async userIds => {
  return cometchatRequest('/cometchat/tenant/group/members', {
    method: 'DELETE',
    body: { user_ids: userIds },
  });
};

/** GET /user/creds */
export const getUserCometchatCreds = async () => {
  return cometchatRequest('/user/creds');
};

/** POST /user/cometchat */
export const provisionCometchatUser = async () => {
  return cometchatRequest('/user/cometchat', { method: 'POST', body: {} });
};

export const COMETCHAT_PERMISSIONS = {
  TENANT_SETTINGS_MANAGE: 'chat.tenant_settings.manage',
};

export default {
  getCometchatGroups,
  getCometchatSites,
  getSiteGroup,
  ensureSiteGroup,
  renameSiteGroup,
  syncSiteGroup,
  deleteSiteGroup,
  getSiteGroupMembers,
  addSiteGroupMembers,
  removeSiteGroupMembers,
  getTenantCometchatSettings,
  updateTenantCometchatSettings,
  getTenantGroup,
  ensureTenantGroup,
  renameTenantGroup,
  deleteTenantGroup,
  syncTenantGroup,
  getTenantGroupMembers,
  addTenantGroupMembers,
  removeTenantGroupMembers,
  getUserCometchatCreds,
  provisionCometchatUser,
  COMETCHAT_PERMISSIONS,
};
